import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { SELECT_OWNER_SEAT } from '@company/graphql/mutations/selectOwnerSeat';
import { PAGE_QUOTA_DEFAULTS } from '@/utils/quotaDefaults';

interface SeatOption {
  type: string;
  count: number;
}

interface SeatSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  currentSeat: string;
  availableSeats: SeatOption[];
  onSuccess?: (newSeatType: string) => void;
  currentUsedPages?: number;
  currentQuota?: number;
}

const SeatSelectionDialog: React.FC<SeatSelectionDialogProps> = ({
  open,
  onClose,
  currentSeat,
  availableSeats,
  onSuccess,
  currentUsedPages = 0,
  currentQuota = 0
}) => {
  const theme = useTheme();
  const [selectedSeat, setSelectedSeat] = useState(currentSeat);
  const [selectOwnerSeat, { loading, error }] = useMutation(SELECT_OWNER_SEAT);

  const seatLabels = {
    S: `Basis (${PAGE_QUOTA_DEFAULTS.S.toLocaleString('de-DE')} Seiten/Monat)`,
    M: `Standard (${PAGE_QUOTA_DEFAULTS.M.toLocaleString('de-DE')} Seiten/Monat)`, 
    L: `Premium (${PAGE_QUOTA_DEFAULTS.L.toLocaleString('de-DE')} Seiten/Monat)`
  };

  const seatColors = {
    S: theme.palette.semantic.planSize.S,
    M: theme.palette.semantic.planSize.M,
    L: theme.palette.semantic.planSize.L
  };

  // Use quota defaults for validation
  const seatQuotas = PAGE_QUOTA_DEFAULTS;

  // Check if a seat type is valid based on current usage
  const isSeatValid = (seatType: string) => {
    return currentUsedPages <= seatQuotas[seatType as keyof typeof seatQuotas];
  };

  const handleConfirm = async () => {
    if (selectedSeat === currentSeat) {
      onClose();
      return;
    }

    try {
      const result = await selectOwnerSeat({
        variables: { seatType: selectedSeat }
      });

      if (result.data?.selectOwnerSeat?.success) {
        onSuccess?.(selectedSeat);
        onClose();
      }
    } catch (err) {
      console.error('Error selecting seat:', err);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Wähle deinen Sitzplatztyp
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Du hast mehrere Sitzplatztypen erworben. Wähle den Typ für dein Konto.
        </Typography>
        {currentUsedPages > 0 && (
          <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
            📊 Aktueller Verbrauch: {currentUsedPages.toLocaleString()} von {currentQuota?.toLocaleString()} Seiten
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Fehler beim Ändern des Sitzplatztyps. Bitte versuche es erneut.
          </Alert>
        )}

        <RadioGroup
          value={selectedSeat}
          onChange={(e) => setSelectedSeat(e.target.value)}
        >
          {availableSeats.map(({ type, count }) => {
            const isValid = isSeatValid(type);
            const isDisabled = !isValid;
            
            return (
            <Card 
              key={type}
              variant="outlined"
              sx={{ 
                mb: 2,
                border: selectedSeat === type 
                  ? `2px solid ${seatColors[type as keyof typeof seatColors]}` 
                  : '1px solid',
                borderColor: selectedSeat === type 
                  ? seatColors[type as keyof typeof seatColors]
                  : isDisabled 
                    ? 'error.main'
                    : 'divider',
                opacity: isDisabled ? 0.7 : 1,
                '&:hover': {
                  borderColor: isDisabled 
                    ? 'error.main' 
                    : seatColors[type as keyof typeof seatColors],
                  boxShadow: isDisabled ? 0 : 1
                }
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <FormControlLabel
                  value={type}
                  control={<Radio color="primary" disabled={isDisabled} />}
                  disabled={isDisabled}
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, flex: 1 }}>
                          Sitzplatz {type}
                        </Typography>
                        <Box 
                          sx={{ 
                            bgcolor: seatColors[type as keyof typeof seatColors],
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            ml: 'auto'
                          }}
                        >
                          {count} verfügbar
                        </Box>
                        {type === currentSeat && (
                          <Box 
                            sx={{ 
                              bgcolor: theme.palette.grey[200],
                              color: theme.palette.grey[700],
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem'
                            }}
                          >
                            Aktuell
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">
                          {seatLabels[type as keyof typeof seatLabels]}
                        </Typography>
                        {isDisabled && (
                          <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
                            Nicht genug Kapazität
                          </Typography>
                        )}
                      </Box>
                      {isDisabled && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                          Du hast {currentUsedPages.toLocaleString()} Seiten verbraucht, aber {type}-Sitze haben nur {seatQuotas[type as keyof typeof seatQuotas].toLocaleString()} Seiten Kapazität.
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ 
                    m: 0,
                    width: '100%',
                    '& .MuiFormControlLabel-label': {
                      width: '100%'
                    }
                  }}
                />
              </CardContent>
            </Card>
            );
          })}
        </RadioGroup>

        <Alert severity="info" sx={{ mt: 2 }}>
          Du kannst deinen Sitzplatztyp später in den Kontoeinstellungen ändern.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={onClose}
          disabled={loading}
          sx={{ mr: 1 }}
        >
          Später entscheiden
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          disabled={loading}
          sx={{ 
            minWidth: 120,
            bgcolor: seatColors[selectedSeat as keyof typeof seatColors]
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            selectedSeat === currentSeat ? 'Bestätigen' : `${selectedSeat} auswählen`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeatSelectionDialog; 